import React from 'react';
import { Listing, Core_Csp_LayoutQueryQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { get, uniq, reduce } from 'lodash';
import Metadata from '@reverbdotcom/commons/src/components/metadata';
import { ReviewMetadata } from '@reverbdotcom/commons/src/components/reviews_metadata';

type Core_Csp_LayoutQueryQuery_Csp = Core_Csp_LayoutQueryQuery['csp'];

interface IProps {
  csp: Core_Csp_LayoutQueryQuery_Csp;
  listings: Listing[];
  loading: boolean;
  condition: string;
}

export default class CSPMetadata extends React.Component<IProps, null> {
  listings() {
    return this.props.listings;
  }

  currency() {
    return get(this.listings()[0], 'pricing.buyerPrice.currency');
  }

  finishes() {
    const finishes = this.listings().map((listing) => {
      return listing.finish;
    });

    return uniq(finishes);
  }

  lowestPrice() {
    if (!this.listings().length) return null;

    const prices = this.listings().map(l => l.pricing.buyerPrice.amountCents / 100);
    return prices.sort()[0].toFixed(2);
  }

  highestPrice() {
    if (!this.listings().length) return null;

    const prices = this.listings().map(l => l.pricing.buyerPrice.amountCents / 100);
    return prices.sort().reverse()[0].toFixed(2);
  }

  models() {
    const models = reduce(
      this.props.csp.specs.productSpecs,
      (result, spec) => {
        if (spec.name === 'Model') {
          result.push(spec.value);
        }
        return result;
      },
      [],
    );

    return uniq(models);
  }

  renderAvailability() {
    if (this.listings().length > 0) {
      return <meta itemProp="availability" content="http://schema.org/InStock" />;
    }
  }

  renderCategories(category, idx) {
    return <meta key={idx} itemProp="category" content={category.name} />;
  }

  renderConditionLink() {
    return this.props.condition === 'used' ? 'http://schema.org/UsedCondition' : 'http://schema.org/NewCondition';
  }

  renderFinishes(finish, idx) {
    return <meta key={idx} itemProp="color" content={finish} />;
  }

  renderModels(model, idx) {
    return <meta key={idx} itemProp="model" content={model} />;
  }

  renderBrandMeta() {
    if (!this.props.csp.brand) return null;

    return (
      <div itemProp="brand" itemType="https://schema.org/Brand" itemScope>
        <meta itemProp="name" content={this.props.csp.brand.name} />
      </div>
    );
  }

  render() {
    const { loading, csp } = this.props;
    if (loading || !csp) return null;

    return (
      <div>
        <Metadata
          title={csp.title}
          description={csp.summary}
          ogDescription={csp.metaDescription}
          image={get(csp, 'cspImage[0].source')}
          metaImage={get(csp, 'supersize_images[0].source')}
        />

        <meta itemProp="name" content={csp.title} />
        <meta itemProp="description" content={csp.summary} />
        {!!csp.categories && csp.categories.map(this.renderCategories)}
        <link href={get(csp, 'cspImage[0].source')} itemProp="image" />
        {this.models().map(this.renderModels)}
        {this.finishes().map(this.renderFinishes)}

        {this.renderBrandMeta()}
        <ReviewMetadata
          totalReviews={csp.productReviewSearch?.stats?.total}
          averageRating={csp.productReviewSearch?.stats?.averageRating}
        />
        {this.listings().length > 0 &&
          <div
            itemProp="offers"
            itemScope
            itemType="http://schema.org/AggregateOffer"
          >
            <link href={this.renderConditionLink()} itemProp="itemCondition" />
            {this.renderAvailability()}
            <meta itemProp="lowPrice" content={this.lowestPrice()} />
            <meta itemProp="highPrice" content={this.highestPrice()} />
            <meta itemProp="priceCurrency" content={this.currency()} />
            <meta itemProp="offerCount" content={`${this.listings().length}`} />
          </div>
        }
      </div>
    );
  }
}
